import {
  useState, useRef, useEffect,
} from 'react';

const useSideDrawerCustomIcon = (
  customIcon: boolean,
  loadingData: boolean,
  navigation?: boolean,
  leadingIcon: boolean = true,
  trailingIcon: boolean = false,
) => {
  const sideDrawerRef = useRef<any>(null);
  const [leadingIconPortal, setLeadingIconPortal] = useState<HTMLDivElement | null>(null);
  const [trailingIconPortal, setTrailingIconPortal] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const removePortal = (
      portal: HTMLDivElement | null,
      reset: (node: HTMLDivElement | null) => void,
    ) => {
      if (portal) {
        portal.remove();
        reset(null);
      }
    };

    const ensurePortal = (
      portal: HTMLDivElement | null,
      setter: (node: HTMLDivElement) => void,
      attach: (node: HTMLDivElement) => void,
    ) => {
      if (portal) return portal;
      const node = document.createElement('div');
      attach(node);
      setter(node);
      return node;
    };

    const el = sideDrawerRef.current;
    if (!el) return;

    const shadow = el.shadowRoot;
    if (!shadow) return;

    const headerTitle = shadow.querySelector('.panel--title');
    if (!headerTitle || !customIcon) {
      removePortal(leadingIconPortal, setLeadingIconPortal);
      removePortal(trailingIconPortal, setTrailingIconPortal);
      return;
    }

    const span = headerTitle.querySelector(':scope > span');

    if (span) {
      span.style.flexGrow = '1';
      span.style.width = '0';
      span.style.maxWidth = 'fit-content';
    }

    if (leadingIcon) {
      ensurePortal(
        leadingIconPortal,
        setLeadingIconPortal,
        (node) => headerTitle.insertBefore(node, headerTitle.lastElementChild),
      );
    } else {
      removePortal(leadingIconPortal, setLeadingIconPortal);
    }

    if (trailingIcon) {
      ensurePortal(
        trailingIconPortal,
        setTrailingIconPortal,
        (node) => headerTitle.appendChild(node),
      );
    } else {
      removePortal(trailingIconPortal, setTrailingIconPortal);
    }
  }, [loadingData, customIcon, sideDrawerRef,
    navigation, leadingIconPortal, trailingIconPortal,
    leadingIcon, trailingIcon]);

  useEffect(() => () => {
    // cleanup portals
    if (leadingIconPortal) {
      leadingIconPortal.remove();
      setLeadingIconPortal(null);
    }
    if (trailingIconPortal) {
      trailingIconPortal.remove();
      setTrailingIconPortal(null);
    }
  }, [leadingIconPortal, trailingIconPortal]);

  return {
    sideDrawerRef, leadingIconPortal, trailingIconPortal,
  };
};

export default useSideDrawerCustomIcon;
